import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, AlertTriangle, Crown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useNavigate } from 'react-router-dom';

// SVG Flag components for reliable cross-platform rendering
const FlagGB = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
    <clipPath id="gb-s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
    <clipPath id="gb-t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
    <g clipPath="url(#gb-s)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#gb-t)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

const FlagES = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 750 500" xmlns="http://www.w3.org/2000/svg">
    <rect width="750" height="500" fill="#c60b1e"/>
    <rect width="750" height="250" fill="#ffc400" y="125"/>
  </svg>
);

const FlagFR = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
    <rect width="900" height="600" fill="#ED2939"/>
    <rect width="600" height="600" fill="#fff"/>
    <rect width="300" height="600" fill="#002395"/>
  </svg>
);

const FlagDE = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 5 3" xmlns="http://www.w3.org/2000/svg">
    <rect width="5" height="3" fill="#FFCE00"/>
    <rect width="5" height="2" fill="#DD0000"/>
    <rect width="5" height="1" fill="#000"/>
  </svg>
);

const FlagIT = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
    <rect width="3" height="2" fill="#009246"/>
    <rect width="2" height="2" x="1" fill="#fff"/>
    <rect width="1" height="2" x="2" fill="#ce2b37"/>
  </svg>
);

const FlagPT = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg">
    <rect width="600" height="400" fill="#ff0000"/>
    <rect width="240" height="400" fill="#006600"/>
  </svg>
);

const FlagPL = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 16 10" xmlns="http://www.w3.org/2000/svg">
    <rect width="16" height="10" fill="#dc143c"/>
    <rect width="16" height="5" fill="#fff"/>
  </svg>
);

const FlagIN = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
    <rect width="900" height="600" fill="#fff"/>
    <rect width="900" height="200" fill="#ff9933"/>
    <rect width="900" height="200" y="400" fill="#138808"/>
    <circle cx="450" cy="300" r="60" fill="#000080"/>
  </svg>
);

const FlagPK = () => (
  <svg className="w-6 h-5 rounded shadow-sm" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
    <rect width="900" height="600" fill="#01411C"/>
    <rect width="225" height="600" fill="#fff"/>
  </svg>
);

const flagComponents = {
  en: FlagGB,
  es: FlagES,
  fr: FlagFR,
  de: FlagDE,
  it: FlagIT,
  pt: FlagPT,
  pl: FlagPL,
  hi: FlagIN,
  pa: FlagIN,
  gu: FlagIN,
  bn: FlagIN,
  ta: FlagIN,
  ur: FlagPK,
};

const languages = [
  // European Languages
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  // Indian Languages (UK market)
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو' },
];

// Warning messages in all supported languages
const trialWarnings = {
  en: {
    title: "Trial Ending Soon",
    message: "Your free trial is ending soon. Multi-language support requires the White Label plan.",
    upgrade: "Upgrade to White Label",
    dismiss: "Remind me later"
  },
  es: {
    title: "Prueba terminando pronto",
    message: "Tu prueba gratuita está por terminar. El soporte multilingüe requiere el plan White Label.",
    upgrade: "Actualizar a White Label",
    dismiss: "Recordarme después"
  },
  fr: {
    title: "Essai se terminant bientôt",
    message: "Votre essai gratuit se termine bientôt. Le support multilingue nécessite le plan White Label.",
    upgrade: "Passer au White Label",
    dismiss: "Me rappeler plus tard"
  },
  de: {
    title: "Testversion endet bald",
    message: "Ihre kostenlose Testversion endet bald. Mehrsprachige Unterstützung erfordert den White Label-Plan.",
    upgrade: "Zu White Label upgraden",
    dismiss: "Später erinnern"
  },
  hi: {
    title: "परीक्षण जल्द समाप्त हो रहा है",
    message: "आपका मुफ्त परीक्षण जल्द समाप्त हो रहा है। बहु-भाषा समर्थन के लिए व्हाइट लेबल योजना आवश्यक है।",
    upgrade: "व्हाइट लेबल में अपग्रेड करें",
    dismiss: "बाद में याद दिलाएं"
  },
  pa: {
    title: "ਅਜ਼ਮਾਇਸ਼ ਜਲਦੀ ਖਤਮ ਹੋ ਰਹੀ ਹੈ",
    message: "ਤੁਹਾਡੀ ਮੁਫ਼ਤ ਅਜ਼ਮਾਇਸ਼ ਜਲਦੀ ਖਤਮ ਹੋ ਰਹੀ ਹੈ। ਬਹੁ-ਭਾਸ਼ਾ ਸਹਾਇਤਾ ਲਈ ਵ੍ਹਾਈਟ ਲੇਬਲ ਯੋਜਨਾ ਲੋੜੀਂਦੀ ਹੈ।",
    upgrade: "ਵ੍ਹਾਈਟ ਲੇਬਲ ਵਿੱਚ ਅੱਪਗ੍ਰੇਡ ਕਰੋ",
    dismiss: "ਬਾਅਦ ਵਿੱਚ ਯਾਦ ਦਿਵਾਓ"
  },
  gu: {
    title: "ટ્રાયલ ટૂંક સમયમાં સમાપ્ત થઈ રહ્યું છે",
    message: "તમારી મફત ટ્રાયલ ટૂંક સમયમાં સમાપ્ત થઈ રહી છે. બહુ-ભાષા સપોર્ટ માટે વ્હાઇટ લેબલ પ્લાન જરૂરી છે.",
    upgrade: "વ્હાઇટ લેબલમાં અપગ્રેડ કરો",
    dismiss: "પછીથી યાદ કરાવો"
  },
  bn: {
    title: "ট্রায়াল শীঘ্রই শেষ হচ্ছে",
    message: "আপনার বিনামূল্যে ট্রায়াল শীঘ্রই শেষ হচ্ছে। বহু-ভাষা সমর্থনের জন্য হোয়াইট লেবেল প্ল্যান প্রয়োজন।",
    upgrade: "হোয়াইট লেবেলে আপগ্রেড করুন",
    dismiss: "পরে মনে করিয়ে দিন"
  },
  ta: {
    title: "சோதனை விரைவில் முடிவடைகிறது",
    message: "உங்கள் இலவச சோதனை விரைவில் முடிவடைகிறது. பல-மொழி ஆதரவுக்கு வைட் லேபிள் திட்டம் தேவை.",
    upgrade: "வைட் லேபிளுக்கு மேம்படுத்துங்கள்",
    dismiss: "பின்னர் நினைவூட்டுங்கள்"
  },
  ur: {
    title: "آزمائش جلد ختم ہو رہی ہے",
    message: "آپ کی مفت آزمائش جلد ختم ہو رہی ہے۔ کثیر لسانی سپورٹ کے لیے وائٹ لیبل پلان درکار ہے۔",
    upgrade: "وائٹ لیبل میں اپگریڈ کریں",
    dismiss: "بعد میں یاد دلائیں"
  },
  it: {
    title: "Prova in scadenza",
    message: "La tua prova gratuita sta per scadere. Il supporto multilingue richiede il piano White Label.",
    upgrade: "Passa a White Label",
    dismiss: "Ricordamelo dopo"
  },
  pt: {
    title: "Teste terminando em breve",
    message: "Seu teste gratuito está terminando. O suporte multilíngue requer o plano White Label.",
    upgrade: "Atualizar para White Label",
    dismiss: "Lembrar depois"
  },
  pl: {
    title: "Okres próbny kończy się wkrótce",
    message: "Twój bezpłatny okres próbny wkrótce się kończy. Wsparcie wielojęzyczne wymaga planu White Label.",
    upgrade: "Przejdź na White Label",
    dismiss: "Przypomnij później"
  }
};

const LanguageSelectorWithPlan = ({ 
  variant = 'default', 
  className = '',
  subscription = null, // { plan: 'white_label' | 'professional' | 'starter', trial_ends_at: Date }
  isCustomer = false // Customers always get free language selection
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [warningShown, setWarningShown] = useState(false);
  
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];
  const CurrentFlag = flagComponents[currentLang.code] || FlagGB;
  
  // Check if company has White Label access
  const hasWhiteLabelAccess = () => {
    if (isCustomer) return true; // Customers always have access
    if (!subscription) return true; // No subscription info = allow (handled by backend)
    
    // White Label plan or on trial
    return subscription.plan === 'white_label' || subscription.status === 'trial';
  };

  // Check if trial is ending soon (within 3 days)
  const isTrialEndingSoon = () => {
    if (!subscription || subscription.status !== 'trial') return false;
    if (!subscription.trial_ends_at) return false;
    
    const trialEnd = new Date(subscription.trial_ends_at);
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    
    return daysLeft <= 3 && daysLeft > 0;
  };

  const handleLanguageChange = (langCode) => {
    // If not customer and no White Label, only allow English
    if (!isCustomer && !hasWhiteLabelAccess() && langCode !== 'en') {
      setShowWarning(true);
      return;
    }
    
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    window.dispatchEvent(new Event('languageChanged'));
    
    // Show trial warning if applicable (only once per session)
    if (isTrialEndingSoon() && langCode !== 'en' && !warningShown) {
      const dismissed = sessionStorage.getItem('languageWarningDismissed');
      if (!dismissed) {
        setShowWarning(true);
        setWarningShown(true);
      }
    }
  };

  const handleDismissWarning = () => {
    setShowWarning(false);
    sessionStorage.setItem('languageWarningDismissed', 'true');
  };

  const handleUpgrade = () => {
    setShowWarning(false);
    navigate('/control-panel/billing');
  };

  // Get warning text in current language
  const warningText = trialWarnings[i18n.language] || trialWarnings.en;
  
  // Check if language selection is restricted
  const isRestricted = !isCustomer && !hasWhiteLabelAccess();

  const renderFlag = (code) => {
    const Flag = flagComponents[code] || FlagGB;
    return <Flag />;
  };

  // Flag-only variant - large visible flag
  if (variant === 'flag-only') {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm"
              className={`h-10 w-10 p-0 rounded-full hover:bg-gray-100/20 relative ${className}`} 
              data-testid="language-selector"
              title={`Language: ${currentLang.name}`}
            >
              <CurrentFlag />
              {isRestricted && (
                <Crown className="h-3 w-3 absolute -top-1 -right-1 text-amber-500" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
            <div className="p-2 border-b mb-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <Globe size={14} />
                Select Language
              </p>
            </div>
            {isRestricted && (
              <>
                <div className="px-2 py-1.5 text-xs text-amber-600 bg-amber-50 rounded mx-1 mb-1 flex items-center gap-1">
                  <Crown className="h-3 w-3" />
                  White Label feature
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            {languages.map((lang) => {
              const isDisabled = isRestricted && lang.code !== 'en';
              return (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => !isDisabled && handleLanguageChange(lang.code)}
                  className={`cursor-pointer flex items-center gap-3 py-2.5 ${
                    i18n.language === lang.code ? 'bg-primary/10 text-primary font-medium' : ''
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`lang-${lang.code}`}
                  disabled={isDisabled}
                >
                  {renderFlag(lang.code)}
                  <div className="flex-1">
                    <p className="font-medium">{lang.name}</p>
                    <p className="text-xs text-gray-500">{lang.nativeName}</p>
                  </div>
                  {isDisabled && <Crown className="h-3 w-3 text-amber-500" />}
                  {i18n.language === lang.code && !isDisabled && (
                    <Check size={18} className="text-primary" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Trial Warning Dialog */}
        <Dialog open={showWarning} onOpenChange={setShowWarning}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                {warningText.title}
              </DialogTitle>
              <DialogDescription className="pt-2">
                {warningText.message}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleDismissWarning} className="flex-1">
                {warningText.dismiss}
              </Button>
              <Button onClick={handleUpgrade} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                <Crown className="h-4 w-4 mr-2" />
                {warningText.upgrade}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (variant === 'compact') {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className={`relative ${className}`} 
              data-testid="language-selector"
              title={`Language: ${currentLang.name}`}
            >
              <CurrentFlag />
              {isRestricted && (
                <Crown className="h-3 w-3 absolute -top-1 -right-1 text-amber-500" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 max-h-80 overflow-y-auto">
            {isRestricted && (
              <>
                <div className="px-2 py-1.5 text-xs text-amber-600 bg-amber-50 rounded mx-1 mb-1 flex items-center gap-1">
                  <Crown className="h-3 w-3" />
                  White Label feature
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            {languages.map((lang) => {
              const isDisabled = isRestricted && lang.code !== 'en';
              return (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => !isDisabled && handleLanguageChange(lang.code)}
                  className={`cursor-pointer flex items-center gap-3 ${
                    i18n.language === lang.code ? 'bg-primary/10 text-primary font-medium' : ''
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`lang-${lang.code}`}
                  disabled={isDisabled}
                >
                  {renderFlag(lang.code)}
                  <span className="flex-1 text-sm">{lang.name}</span>
                  {isDisabled && <Crown className="h-3 w-3 text-amber-500" />}
                  {i18n.language === lang.code && !isDisabled && (
                    <Check size={16} className="text-primary" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Trial Warning Dialog */}
        <Dialog open={showWarning} onOpenChange={setShowWarning}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                {warningText.title}
              </DialogTitle>
              <DialogDescription className="pt-2">
                {warningText.message}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleDismissWarning} className="flex-1">
                {warningText.dismiss}
              </Button>
              <Button onClick={handleUpgrade} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                <Crown className="h-4 w-4 mr-2" />
                {warningText.upgrade}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Default variant (full button with flag and label)
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className={`gap-2 min-w-[140px] justify-start ${className}`} 
            data-testid="language-selector"
          >
            <CurrentFlag />
            <span className="flex-1 text-left">{currentLang.name}</span>
            {isRestricted && <Crown className="h-3 w-3 text-amber-500" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
          <div className="p-2 border-b mb-2">
            <p className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
              <Globe size={14} />
              Select Language
            </p>
          </div>
          {isRestricted && (
            <>
              <div className="px-2 py-1.5 text-xs text-amber-600 bg-amber-50 rounded mx-1 mb-1 flex items-center gap-1">
                <Crown className="h-3 w-3" />
                White Label feature - Upgrade to unlock
              </div>
              <DropdownMenuSeparator />
            </>
          )}
          {languages.map((lang) => {
            const isDisabled = isRestricted && lang.code !== 'en';
            return (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => !isDisabled && handleLanguageChange(lang.code)}
                className={`cursor-pointer flex items-center gap-3 py-2.5 ${
                  i18n.language === lang.code ? 'bg-primary/10 text-primary font-medium' : ''
                } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`lang-${lang.code}`}
                disabled={isDisabled}
              >
                {renderFlag(lang.code)}
                <div className="flex-1">
                  <p className="font-medium">{lang.name}</p>
                  <p className="text-xs text-gray-500">{lang.nativeName}</p>
                </div>
                {isDisabled && <Crown className="h-3 w-3 text-amber-500" />}
                {i18n.language === lang.code && !isDisabled && (
                  <Check size={18} className="text-primary" />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Trial Warning Dialog */}
      <Dialog open={showWarning} onOpenChange={setShowWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              {warningText.title}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {warningText.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleDismissWarning} className="flex-1">
              {warningText.dismiss}
            </Button>
            <Button onClick={handleUpgrade} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
              <Crown className="h-4 w-4 mr-2" />
              {warningText.upgrade}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LanguageSelectorWithPlan;
